import { createApp } from 'vue';
import App from './App.vue';
import i18n from '@/i18n';
import router from '@/router';
import vuetify from '@/vuetify';
import store from '@/store';
import Utils from '@/modules/utils';
import { ApolloClient, createHttpLink, InMemoryCache, split } from '@apollo/client/core';
import { provideApolloClient } from '@vue/apollo-composable';
import VueApolloComponents from '@vue/apollo-components';
import Vue3Autocounter from 'vue3-autocounter';
import { onError } from '@apollo/client/link/error';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { apiBaseUrl, browserHeaders, getGraphqlWebSocketUrl } from '@/modules/session';

function getHeaders () {
  return { ...browserHeaders };
}

const httpLink = createHttpLink({
  uri: apiBaseUrl + '/graphql',
  credentials: 'include',
  fetch: (uri, options) => {
    options.headers = getHeaders();
    return fetch(uri, options);
  },
});

const wsLink = new WebSocketLink({
  uri: getGraphqlWebSocketUrl(),
  fetch: (uri, options) => {
    options.headers = getHeaders();
    return fetch(uri, options);
  },
  options: {
    reconnect: true,
  },
});

const errorLink = onError(error => {
  if (error.graphQLErrors) {
    error.graphQLErrors.forEach(err => {
      if (err.message === 'UNAUTHORIZED' || err.message === 'EXPIRED_TOKEN') {
        // Confirm session expiry through the session endpoint. Network/server errors keep identity intact.
        store.dispatch('refreshSession').catch(() => {});
      } else if (process.env.NODE_ENV !== 'production') {
        console.error(err);
      }
    });
  } else if (process.env.NODE_ENV !== 'production') {
    console.log(error);
  }
});

const link = split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink,
);

// Cache implementation
const cache = new InMemoryCache();

// Create the apollo client
const apolloClient = new ApolloClient({
  link: errorLink.concat(link),
  cache,
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache',
    },
    watchQuery: {
      fetchPolicy: 'no-cache',
    },
  },
});

const app = createApp(App)
  .use(i18n)
  .use(router)
  .use(vuetify)
  .use(store)
  .use(Utils)
  .use(VueApolloComponents)
  .component('vue3-autocounter', Vue3Autocounter);

provideApolloClient(apolloClient);

app.config.performance = true;

router.isReady().then(() => app.mount('#app'));

const synchronizeSession = () => {
  if (document.visibilityState === 'hidden') return;
  store.dispatch('refreshSession').catch(() => {});
};
window.addEventListener('online', synchronizeSession);
window.addEventListener('focus', synchronizeSession);
document.addEventListener('visibilitychange', synchronizeSession);
setInterval(synchronizeSession, 5 * 60 * 1000);
